import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { styled, TextField, TextFieldProps } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const StyledDatePicker = styled(DatePicker)`
  button:disabled {
    border: none;
    background-image: none;
  }
`;

type Props = TextFieldProps & {
  control: Control<any>;
  name: string;
  label: string;
  disabled?: boolean;
  onChange?: (date: any) => void;
  shouldDisableDate?: (date: any) => boolean;
  required?: boolean;
  fullWidth?: boolean;
  minDate?: Date | string;
  maxDate?: string;
};

export default function ControlledDatePicker({
  control,
  name,
  label,
  disabled,
  variant = 'outlined',
  onChange,
  shouldDisableDate,
  fullWidth,
  minDate,
  maxDate,
  required
}: Props) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => (
          <StyledDatePicker
            {...field}
            views={['year', 'month', 'day']}
            ref={control.register(name)?.ref}
            disabled={disabled}
            sx={{ width: fullWidth ? '100%' : '160px' }}
            label={label}
            shouldDisableDate={shouldDisableDate}
            onChange={(value: any) => {
              field.onChange(value);
              if (dayjs(value).isValid()) onChange?.(dayjs(value));
            }}
            minDate={minDate}
            maxDate={maxDate}
            renderInput={(props) => {
              return (
                <TextField
                  variant={variant}
                  {...props}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  size="small"
                  required={required}
                />
              );
            }}
          />
        )}
      />
    </LocalizationProvider>
  );
}
